/**
 * LoginPage.jsx — DisclosureOS Authentication
 * ═════════════════════════════════════════════
 * 
 * Route: /login
 * API:   POST /api/auth/login     (email + password)
 *        POST /api/auth/signup    (registration)
 *        GET  /api/auth/me        (token validation)
 * 
 * Integrates with api_service.js AuthProvider:
 *   - useAuth().login(email, password)
 *   - useAuth().loginSSO(token)
 *   - Post-auth destination: ?next= (validated) or the default home
 *     (services/loginRouting.jsx — interim /dashboard pending the canonical
 *     Home decision in docs/CANONICAL_SURFACES.md)
 */

import { useState, useCallback, useEffect } from "react";
import { useAuth, apiFetch } from "../services/api_service";
import { resolvePostLoginDestination } from "../services/loginRouting";

const C = {
  bg: "#050a14", surface: "#0c1220", card: "#111c30", cardHover: "#162040",
  border: "#1a2744", borderHi: "#2a3a5c", text: "#e2e8f0", muted: "#6b7a96",
  dim: "#384660", blue: "#3b82f6", amber: "#f59e0b", emerald: "#10b981",
  purple: "#8b5cf6", cyan: "#06b6d4", red: "#ef4444", teal: "#14b8a6",
  white: "#fff", indigo: "#6366f1",
};

const REGULATIONS = [
  { name: "CSRD", color: C.blue },
  { name: "CSDDD", color: C.red },
  { name: "EU Taxonomy", color: C.emerald },
  { name: "SFDR", color: C.indigo },
  { name: "GRI", color: C.teal },
  { name: "TCFD", color: C.amber },
];

export default function LoginPage() {
  const { user, login, loginSSO, loading: authLoading } = useAuth();
  const [mode, setMode] = useState("login"); // login | signup | sso
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [org, setOrg] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Redirect if already authenticated — returning users go back to their
  // intended destination (?next=), everyone else to the default home.
  useEffect(() => {
    if (user && !authLoading) {
      const next = new URLSearchParams(window.location.search).get("next");
      window.location.replace(resolvePostLoginDestination(next));
    }
  }, [user, authLoading]);

  // SSO: check URL for token param (from OAuth redirect)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get("sso_token");
    if (ssoToken) {
      setMode("sso");
      setLoading(true);
      loginSSO(ssoToken).then(() => {
        setSuccess("SSO authentication successful. Redirecting…");
      }).catch(e => {
        setError("SSO token invalid or expired. Please try again.");
      }).finally(() => setLoading(false));
    }
  }, [loginSSO]);

  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    if (!email || !password) { setError("Email and password are required"); return; }
    setLoading(true);
    setError(null);
    try {
      await login(email, password);
      setSuccess("Authenticated. Loading platform…");
    } catch (err) {
      setError(err.message || "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  }, [email, password, login]);

  const handleSignup = useCallback(async (e) => {
    e.preventDefault();
    if (!email || !password || !name) { setError("All fields are required"); return; }
    if (!inviteCode.trim()) { setError("An invitation code is required — ask your administrator"); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }
    setLoading(true);
    setError(null);
    try {
      const signupUrl = (process.env.REACT_APP_BACKEND_URL || "") + "/api/auth/signup";
      const resp = await fetch(signupUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, name, organization: org, invitation_token: inviteCode.trim() }),
      });
      if (!resp.ok) { const d = await resp.json().catch(() => ({})); throw new Error(d.detail || "Signup failed"); }
      setSuccess("Account created. Logging in…");
      await login(email, password);
    } catch (err) {
      setError(err.message || "Signup failed.");
    } finally {
      setLoading(false);
    }
  }, [email, password, name, org, inviteCode, login]);

  if (authLoading) return (
    <div style={{ background: C.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", color: C.muted }}>
        <div style={{ width: 24, height: 24, border: `2px solid ${C.border}`, borderTopColor: C.cyan, borderRadius: "50%", animation: "spin .8s linear infinite", margin: "0 auto 8px" }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{ fontSize: 11 }}>Verifying session…</div>
      </div>
    </div>
  );

  return (
    <div style={{ background: C.bg, minHeight: "100vh", display: "flex", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      {/* Left: Login Form */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 20px" }}>
        <div style={{ width: "100%", maxWidth: 380 }}>
          {/* Logo */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 32 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: `linear-gradient(135deg, ${C.teal}, ${C.blue})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 800, color: C.white }}>D</div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>DisclosureOS</div>
              <div style={{ fontSize: 9, color: C.dim, letterSpacing: 1.2, textTransform: "uppercase" }}>ESG Compliance Platform</div>
            </div>
          </div>

          {/* Tab Toggle */}
          <div style={{ display: "flex", gap: 2, marginBottom: 20, background: C.surface, borderRadius: 6, padding: 2 }}>
            {[{ id: "login", l: "Sign In" }, { id: "signup", l: "Create Account" }].map(t => (
              <button key={t.id} onClick={() => { setMode(t.id); setError(null); setSuccess(null); }}
                style={{ flex: 1, padding: "8px 0", borderRadius: 5, border: "none", background: mode === t.id ? C.card : "transparent", color: mode === t.id ? C.text : C.muted, fontSize: 11, fontWeight: 600, cursor: "pointer", transition: "all .2s" }}>
                {t.l}
              </button>
            ))}
          </div>

          {/* Error / Success */}
          {error && (
            <div style={{ padding: "8px 12px", borderRadius: 6, background: C.red + "08", border: `1px solid ${C.red}22`, marginBottom: 12, fontSize: 10, color: C.red }}>
              {error}
            </div>
          )}
          {success && (
            <div style={{ padding: "8px 12px", borderRadius: 6, background: C.emerald + "08", border: `1px solid ${C.emerald}22`, marginBottom: 12, fontSize: 10, color: C.emerald }}>
              {success}
            </div>
          )}

          {/* Login Form */}
          {mode === "login" && (
            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 10, color: C.muted, marginBottom: 4 }}>Work email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" autoComplete="email" autoFocus
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 10, color: C.muted, marginBottom: 4 }}>Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 8 characters" autoComplete="current-password"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
              </div>
              <button data-testid="login-submit-btn" type="submit" disabled={loading}
                style={{ width: "100%", padding: "11px 0", borderRadius: 6, border: "none", background: loading ? C.dim : `linear-gradient(135deg, ${C.teal}, ${C.blue})`, color: C.white, fontSize: 12, fontWeight: 700, cursor: loading ? "wait" : "pointer", marginBottom: 12 }}>
                {loading ? "Authenticating…" : "Sign In"}
              </button>
            </form>
          )}

          {/* Signup Form */}
          {mode === "signup" && (
            <form onSubmit={handleSignup}>
              <div style={{ marginBottom: 10 }}>
                <label style={{ display: "block", fontSize: 10, color: C.muted, marginBottom: 4 }}>Full name</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Jane Smith" autoComplete="name" autoFocus
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={{ display: "block", fontSize: 10, color: C.muted, marginBottom: 4 }}>Work email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" autoComplete="email"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={{ display: "block", fontSize: 10, color: C.muted, marginBottom: 4 }}>Organisation</label>
                <input type="text" value={org} onChange={e => setOrg(e.target.value)} placeholder="Acme Corp (optional)"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={{ display: "block", fontSize: 10, color: C.muted, marginBottom: 4 }}>Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 8 characters" autoComplete="new-password"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 10, color: C.muted, marginBottom: 4 }}>Invitation code</label>
                <input type="text" value={inviteCode} onChange={e => setInviteCode(e.target.value)} placeholder="From your administrator's invitation" autoComplete="off"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 12, outline: "none", boxSizing: "border-box", fontFamily: "'JetBrains Mono', monospace" }} />
              </div>
              <button type="submit" disabled={loading}
                style={{ width: "100%", padding: "11px 0", borderRadius: 6, border: "none", background: loading ? C.dim : C.emerald, color: C.white, fontSize: 12, fontWeight: 700, cursor: loading ? "wait" : "pointer", marginBottom: 12 }}>
                {loading ? "Creating account…" : "Create Account"}
              </button>
            </form>
          )}

          {/* Divider */}
          {/* Footer */}
          <div style={{ marginTop: 24, fontSize: 8, color: C.dim, textAlign: "center", lineHeight: 1.6 }}>
            By signing in you agree to the DisclosureOS Terms of Service and Privacy Policy.
            <br />Your data is encrypted at rest and in transit.
          </div>
        </div>
      </div>

      {/* Right: Brand Panel */}
      <div style={{ width: 420, background: `linear-gradient(145deg, ${C.surface}, ${C.bg})`, borderLeft: `1px solid ${C.border}`, display: "flex", flexDirection: "column", justifyContent: "center", padding: "40px 36px" }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: C.text, lineHeight: 1.3, marginBottom: 8 }}>
          One datapoint.<br />
          <span style={{ color: C.teal }}>Every framework.</span><br />
          Zero duplicate effort.
        </div>
        <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.6, marginBottom: 24 }}>
          Enter your ESG data once. DisclosureOS maps it across all mandatory and voluntary frameworks simultaneously — CSRD, CSDDD, EU Taxonomy, SFDR, GRI, TCFD, and more.
        </div>

        {/* Regulation Pills */}
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 24 }}>
          {REGULATIONS.map(r => (
            <span key={r.name} style={{ padding: "3px 10px", borderRadius: 99, fontSize: 9, fontWeight: 600, background: r.color + "12", color: r.color, border: `1px solid ${r.color}22` }}>{r.name}</span>
          ))}
        </div>

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 24 }}>
          {[
            { v: "12+", l: "Frameworks mapped" },
            { v: "90", l: "Industry assessments" },
            { v: "18", l: "SFDR PAI indicators" },
            { v: "<1s", l: "Cascade propagation" },
          ].map((s, i) => (
            <div key={i} style={{ padding: "10px 12px", borderRadius: 6, background: C.card, border: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.cyan }}>{s.v}</div>
              <div style={{ fontSize: 8, color: C.muted }}>{s.l}</div>
            </div>
          ))}
        </div>

        {/* Trust Badges */}
        <div style={{ display: "flex", gap: 8, fontSize: 8, color: C.dim }}>
          <span>Encrypted in transit &amp; at rest</span>
          <span style={{ color: C.border }}>|</span>
          <span>EU-hosted</span>
          <span style={{ color: C.border }}>|</span>
          <span>Full audit trail</span>
        </div>
      </div>
    </div>
  );
}
